import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Factory,
  Tag,
  Calendar,
  Gauge,
  Cog,
  FileText,
  Hash,
  Truck,
  ChevronLeft,
  ChevronRight,
  Expand,
  X,
} from 'lucide-react';
import { api } from '../lib/api';
import { SEO } from '../components/SEO';
import { EnquiryForm } from '../components/EnquiryForm';
import { formatPrice, displayPrice, formatKm } from '../lib/format';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';

const SPEC_ROWS = [
  { key: 'make', label: 'Make', icon: Factory },
  { key: 'model', label: 'Model', icon: Tag },
  { key: 'year', label: 'Year', icon: Calendar },
  { key: 'kilometres', label: 'Kilometres', icon: Gauge, format: (v) => formatKm(v) },
  { key: 'transmission', label: 'Transmission', icon: Cog },
  { key: 'registration', label: 'Registration', icon: FileText },
  { key: 'stock_number', label: 'Stock Number', icon: Hash },
  { key: 'vehicle_type', label: 'Vehicle Type', icon: Truck },
];

export default function TruckDetail() {
  const { slug } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setActiveIdx(0);
    api
      .get(`/vehicles/slug/${slug}`)
      .then((res) => setVehicle(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  const images = vehicle?.images || [];
  const next = useCallback(() => setActiveIdx((i) => (i + 1) % Math.max(images.length, 1)), [images.length]);
  const prev = useCallback(
    () => setActiveIdx((i) => (i - 1 + Math.max(images.length, 1)) % Math.max(images.length, 1)),
    [images.length]
  );

  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, next, prev]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Skeleton className="h-5 w-64 mb-6" />
        <div className="lg:grid lg:grid-cols-2 lg:gap-10">
          <div className="space-y-3">
            <Skeleton className="aspect-[4/3] rounded-xl" />
            <div className="grid grid-cols-4 gap-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="aspect-[4/3] rounded-lg" />
              ))}
            </div>
          </div>
          <div className="space-y-4 mt-8 lg:mt-0">
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !vehicle) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center" data-testid="vehicle-not-found">
        <Truck className="h-14 w-14 text-[#D9D9D6] mx-auto mb-4" />
        <h1 className="font-heading text-3xl tracking-wide text-[#24135F]">VEHICLE NOT FOUND</h1>
        <p className="text-[#53565A] mt-2 mb-8">This vehicle may have been sold or removed.</p>
        <Button asChild className="rounded-full bg-[#E4002B] hover:bg-[#E4002B]/90 h-11 px-8 font-bold tracking-wider">
          <Link to="/trucks">BROWSE ALL TRUCKS</Link>
        </Button>
      </div>
    );
  }

  const isSold = vehicle.status === 'Sold';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name: vehicle.title,
    brand: { '@type': 'Brand', name: vehicle.make },
    model: vehicle.model,
    vehicleModelDate: String(vehicle.year),
    mileageFromOdometer: { '@type': 'QuantitativeValue', value: vehicle.kilometres, unitCode: 'KMT' },
    offers: {
      '@type': 'Offer',
      ...(vehicle.poa ? {} : { price: vehicle.price }),
      priceCurrency: 'AUD',
      availability: isSold ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
    },
  };

  return (
    <>
      <SEO
        title={vehicle.title}
        description={`${vehicle.year} ${vehicle.make} ${vehicle.model} for sale - ${vehicle.poa ? 'POA' : `${formatPrice(vehicle.price)} +GST`}, ${formatKm(vehicle.kilometres)}. ${(vehicle.description || '').slice(0, 120)}`}
        path={`/trucks/${vehicle.slug}`}
        jsonLd={jsonLd}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <Breadcrumb data-testid="page-breadcrumb">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/trucks">Trucks for Sale</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{vehicle.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="lg:grid lg:grid-cols-2 lg:gap-10 mt-6">
          {/* Gallery */}
          <div>
            <div className="relative aspect-[4/3] bg-[#D9D9D6]/30 rounded-xl overflow-hidden group">
              {images.length > 0 ? (
                <img
                  src={images[activeIdx]?.image_data}
                  alt={`${vehicle.title} photo ${activeIdx + 1}`}
                  data-testid="vehicle-gallery-main-image"
                  className="w-full h-full object-cover cursor-zoom-in"
                  onClick={() => setLightbox(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#D9D9D6]">
                  <Truck className="h-16 w-16" />
                </div>
              )}
              {isSold && (
                <span className="absolute top-4 left-4 bg-[#E4002B] text-white text-sm font-bold px-4 py-1.5 rounded-full tracking-wider">
                  SOLD
                </span>
              )}
              {images.length > 0 && (
                <button
                  data-testid="vehicle-gallery-open-lightbox-button"
                  onClick={() => setLightbox(true)}
                  className="absolute bottom-4 right-4 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full transition-colors duration-150"
                  aria-label="View fullscreen"
                >
                  <Expand className="h-4 w-4" />
                </button>
              )}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    aria-label="Previous image"
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors duration-150 opacity-0 group-hover:opacity-100"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={next}
                    aria-label="Next image"
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors duration-150 opacity-0 group-hover:opacity-100"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-3 mt-3">
                {images.map((img, idx) => (
                  <button
                    key={img.id}
                    data-testid="vehicle-gallery-thumbnail"
                    onClick={() => setActiveIdx(idx)}
                    className={`aspect-[4/3] rounded-lg overflow-hidden border-2 transition-colors duration-150 ${
                      idx === activeIdx ? 'border-[#E4002B]' : 'border-transparent hover:border-[#D9D9D6]'
                    }`}
                  >
                    <img src={img.image_data} alt={`Thumbnail ${idx + 1}`} loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Description (desktop position) */}
            <div className="mt-8 hidden lg:block">
              <h2 className="font-heading text-3xl tracking-wider text-[#24135F] mb-3">DESCRIPTION</h2>
              <p className="text-sm text-[#53565A] leading-relaxed whitespace-pre-line" data-testid="vehicle-description">
                {vehicle.description || 'No description provided.'}
              </p>
            </div>
          </div>

          {/* Info + enquiry */}
          <div className="mt-8 lg:mt-0">
            <h1 className="font-heading text-3xl sm:text-4xl tracking-wider text-[#24135F]" data-testid="vehicle-title">
              {vehicle.title}
            </h1>
            <p className="mt-2 flex items-center gap-2">
              <span className="font-heading text-4xl text-[#E4002B] tracking-wide" data-testid="vehicle-price">
                {displayPrice(vehicle)}
              </span>
              {!vehicle.poa && <span className="text-[#E4002B] text-sm font-bold">+GST</span>}
              {isSold && (
                <span className="ml-2 bg-[#E4002B] text-white text-xs font-bold px-3 py-1 rounded-full tracking-wider">
                  SOLD
                </span>
              )}
            </p>
            {vehicle.poa && !isSold && (
              <p className="mt-1 text-sm text-[#53565A]" data-testid="vehicle-poa-note">
                Price on application - send an enquiry below to discuss pricing or make an offer.
              </p>
            )}

            <div className="mt-6 bg-white border border-[#D9D9D6] rounded-xl divide-y divide-[#D9D9D6]/50" data-testid="vehicle-specs">
              {SPEC_ROWS.map((row) => {
                const raw = vehicle[row.key];
                if (raw === undefined || raw === null || raw === '') return null;
                const value = row.format ? row.format(raw) : raw;
                return (
                  <div key={row.key} className="flex items-center justify-between px-4 py-3">
                    <span className="flex items-center gap-3 text-sm text-[#53565A]">
                      <span className="h-8 w-8 rounded-full border border-[#E4002B]/40 flex items-center justify-center">
                        <row.icon className="h-3.5 w-3.5 text-[#E4002B]" />
                      </span>
                      {row.label}
                    </span>
                    <span className="text-sm font-semibold text-[#24135F]">{value}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 bg-white border border-[#D9D9D6] rounded-xl p-6" data-testid="vehicle-enquiry-card">
              <h2 className="font-heading text-2xl tracking-wider text-[#24135F] mb-1">
                ENQUIRE ABOUT THIS VEHICLE
              </h2>
              {isSold && (
                <p className="text-xs text-[#53565A] mb-3">
                  This vehicle has been sold, but enquire below and we'll help you find something similar.
                </p>
              )}
              <div className="mt-4">
                <EnquiryForm vehicleId={vehicle.id} vehicleTitle={vehicle.title} compact />
              </div>
            </div>
          </div>
        </div>

        {/* Description (mobile position) */}
        <div className="mt-8 lg:hidden">
          <h2 className="font-heading text-3xl tracking-wider text-[#24135F] mb-3">DESCRIPTION</h2>
          <p className="text-sm text-[#53565A] leading-relaxed whitespace-pre-line">
            {vehicle.description || 'No description provided.'}
          </p>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={lightbox} onOpenChange={setLightbox}>
        <DialogContent
          data-testid="vehicle-gallery-lightbox"
          className="max-w-[95vw] sm:max-w-5xl p-0 bg-black border-none overflow-hidden [&>button]:hidden"
        >
          <div className="relative">
            <img
              src={images[activeIdx]?.image_data}
              alt={`${vehicle.title} fullscreen ${activeIdx + 1}`}
              className="w-full max-h-[85vh] object-contain"
            />
            <button
              onClick={() => setLightbox(false)}
              aria-label="Close"
              className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-colors duration-150"
            >
              <X className="h-5 w-5" />
            </button>
            {images.length > 1 && (
              <>
                <button
                  onClick={prev}
                  aria-label="Previous image"
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full transition-colors duration-150"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={next}
                  aria-label="Next image"
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full transition-colors duration-150"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <span className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
                  {activeIdx + 1} / {images.length}
                </span>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
